import React, {useEffect, useState} from "react";
import "./currentSensorReliability.scss";
import {DashboardCard} from "../../../../components/DashboardCard/DashboardCard";
import {Autocomplete, Button, TextField, Typography} from "@mui/material";
import SpeedIcon from '@mui/icons-material/Speed';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TimelineIcon from '@mui/icons-material/Timeline';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import {
    FleetSensorReliabilitySummary,
    Organization,
    SensorFailureCategory,
    SensorType,
    FlowDirection,
    FrameSensorReliabilitySummary
} from "../../../../autogenerate/api.generated.clients";
import {useUserParentOrganizations} from "../../../../hooks/Organization/useUserParentOrganizations";
import {useGetIsArielEmployee} from "../../../../hooks/Organization/useGetIsArielEmployee";
import OrganizationsAutoComplete from "../../../../components/OrganizationsAutoComplete/OrganizationsAutoComplete";
import {DonutChart, DonutData} from "../../../../components/DonutChart/DonutChart";
import GenericReportGrid, {ReportFilter, TemplateRow} from "../../../../components/GenericReportGrid/GenericReportGrid";
import {useSensorReliability} from "../../../../hooks/SensorReliability/useSensorReliability";
import {convertDateStringToLocalTime} from "../../../../utilities/dateHelper";
import {Column} from "@syncfusion/ej2-react-grids";

interface SensorFailureInstanceWithDetails {
    description: string;
    timeFailed: string;
    estimatedHoursAtFailure: string;
    category: string;
    sensorType: string;
    side: string;
    frameSerialNumber: string;
    throwNumber: number;
    alerts: AssociatedAlert[];
    associatedAlertIndex: string;
}

interface AssociatedAlert {
    alertText: string;
    associatedAlertIndex: string;
}

export interface SensorFailureSummary {
    totalSensors: string | null;
    failedSensors: string | null;
    newFailureCount: string | null;
    failureRate: string | null;
    isNewFailuresGreaterThanZero: boolean;
}

const sensorStatusAndColorData = [
    {status: 'Valve Cap', color: '#FF6F61', prediction: 'valveCapSensorFailureCount', count: 0},
    {status: 'Cylinder', color: '#6B8E23', prediction: 'cylinderSensorFailureCount', count: 0},
    {status: 'Speed', color: '#4682B4', prediction: 'speedSensorFailureCount', count: 0},
    {status: 'Main Bearing', color: '#D2691E', prediction: 'mainBearingSensorFailureCount', count: 0},
    {status: 'Oil', color: '#8FBC8F', prediction: 'oilSensorFailureCount', count: 0},
    {status: 'Packing Vent', color: '#6495ED', prediction: 'packingVentSensorFailureCount', count: 0},
    {status: 'Thrust Bearing', color: '#CD5C5C', prediction: 'thrustBearingSensorFailureCount', count: 0},
] as DonutData[];

const sensorColumns = [
    {field: 'frameSerialNumber', headerText: 'Frame Serial Number', width: 'auto', textAlign: 'Left'},
    {field: 'description', headerText: 'MeasurementDescription', width: 'auto', textAlign: 'Left'},
    {field: 'timeFailed', headerText: 'Time Failed', width: 'auto', textAlign: 'Right'},
    {field: 'estimatedHoursAtFailure', headerText: 'Estimated Hours at Failure', width: 'auto', textAlign: 'Right'},
    {field: 'category', headerText: 'Category', width: 'auto', textAlign: 'Left'},
    {field: 'sensorType', headerText: 'Sensor Type', width: 'auto', textAlign: 'Left'},
    {field: 'side', headerText: 'Side', width: 'auto', textAlign: 'Left'},
];

const template = (args: any) => {
    return (
        <Button
            size={"small"}
            variant={"outlined"}
            href={`/compressors/${args.frameSerialNumber}/charts`}
        >
            {'Chart Data'}
        </Button>
    )
}

const templateRow = [
    {field: 'chartData', headerText: "", templateFunction: template} as TemplateRow,
]

const childColumns = [
    {field: 'alertText', headerText: 'Associated Alert', width: 'auto', textAlign: 'Left'},
] as Column[];

function CurrentSensorReliability () {
    const [subOrg, setSubOrg] = useState<Organization | null>(null);
    const [compressorOptions, setCompressorOptions] = useState<string[]>([]);
    const [selectedCompressors, setSelectedCompressors] = useState<string[]>([]);
    const parentOrganizations = useUserParentOrganizations().data;
    const isArielEmployee = useGetIsArielEmployee().data;
    const sensorReliability = useSensorReliability(subOrg?.externalId);
    const [failedSensorByTypeData, setFailedSensorByTypeData] = useState<DonutData[]>([] as DonutData[]);
    const [sensorFailureDetails, setSensorFailureDetails] = useState<SensorFailureInstanceWithDetails[]>([] as SensorFailureInstanceWithDetails[]);
    const [categoryFilter, setCategoryFilter] = useState<ReportFilter | undefined>();
    const [sensorFailureSummaries, setSensorFailureSummaries] = useState<SensorFailureSummary | null>(null);
    const [associatedAlerts, setAssociatedAlerts] = useState<AssociatedAlert[]>([] as AssociatedAlert[]);
    
    useEffect(() => {
        setDefaultValues();
    }, [sensorReliability.data]);

    useEffect(() => {
        if(sensorReliability.data) {
            if(selectedCompressors.length === 0) {
                setDefaultValues();
            } else {
                updateDataForSelectedCompressors();
            }   
        }
    }, [selectedCompressors]);
    
    const setDefaultValues = () => {
        if(sensorReliability.data) {
            const options = sensorReliability.data.frameSensorReliabilitySummaries.map(frame => frame.frameSerialNumber) ?? [];
            const newFailureCount =  formatNumberWithCommas(sensorReliability.data.newFailures);
            const isNewFailuresGreaterThanZero = sensorReliability.data.newFailures !== undefined && sensorReliability.data.newFailures !== null && sensorReliability.data.newFailures > 0;
            const failureRate = `${((sensorReliability.data.failedSensors / sensorReliability.data.totalSensors) * 100).toFixed(2)}%`;
            const totalSensors = formatNumberWithCommas(sensorReliability.data.totalSensors);
            const failedSensors = formatNumberWithCommas(sensorReliability.data.failedSensors);
            setSensorFailureSummaries({
                totalSensors,
                failedSensors,
                newFailureCount,
                failureRate,
                isNewFailuresGreaterThanZero
            });
            setCompressorOptions(options);
            setFailedSensorByTypeData(sensorStatusAndColorData.map((item) => (
                {
                    ...item,
                    count: sensorReliability.data[item.prediction as keyof FleetSensorReliabilitySummary] as number || 0
                })))
            
            const sensorFailureInstancesWithDetails : SensorFailureInstanceWithDetails[] = getSensorFailureInstancesWithDetails(sensorReliability.data.frameSensorReliabilitySummaries);
            setSensorFailureDetails(sensorFailureInstancesWithDetails);
            setAssociatedAlerts(getAlertArrayChildData(sensorFailureInstancesWithDetails));

        }
    }
    
    const updateDataForSelectedCompressors = () => {
        const frameSensorReliabilitySummaries = sensorReliability.data!.frameSensorReliabilitySummaries.filter(frameSummary => selectedCompressors.includes(frameSummary.frameSerialNumber));
        const newFailureCount = frameSensorReliabilitySummaries ? frameSensorReliabilitySummaries.reduce((sum, frameSummary) => (sum + frameSummary.newFailureCount), 0) : null;
        const isNewFailuresGreaterThanZero = newFailureCount !== undefined && newFailureCount !== null && newFailureCount > 0;
        const totalSensors = frameSensorReliabilitySummaries ? frameSensorReliabilitySummaries.reduce((sum, frameSummary) => (sum + frameSummary.totalSensors), 0) : null;
        const failedSensors = frameSensorReliabilitySummaries ? frameSensorReliabilitySummaries.reduce((sum, frameSummary) => (sum + frameSummary.failedSensors), 0) : null;
        const failureRate = frameSensorReliabilitySummaries && failedSensors && totalSensors ? `${((failedSensors / totalSensors) * 100).toFixed(2)}%` : null;

        setSensorFailureSummaries({
            totalSensors: totalSensors ? formatNumberWithCommas(totalSensors) : null, 
            failedSensors: failedSensors ? formatNumberWithCommas(failedSensors) : null, 
            newFailureCount: newFailureCount ? formatNumberWithCommas(newFailureCount) : null, 
            failureRate, 
            isNewFailuresGreaterThanZero
        });
        setFailedSensorByTypeData(sensorStatusAndColorData.map((item) => (
            {
                ...item,
                count: frameSensorReliabilitySummaries.reduce((sum, frameSummary) => sum + (frameSummary[item.prediction as keyof FrameSensorReliabilitySummary] as number), 0)
            })))
        const sensorFailureInstancesWithDetails : SensorFailureInstanceWithDetails[] = getSensorFailureInstancesWithDetails(frameSensorReliabilitySummaries);
        setSensorFailureDetails(sensorFailureInstancesWithDetails);
        setAssociatedAlerts(getAlertArrayChildData(sensorFailureInstancesWithDetails));
    }
    
    const getAlertArrayChildData = (sensorFailureInstancesWithDetails : SensorFailureInstanceWithDetails[]) => {
        return sensorFailureInstancesWithDetails.flatMap(sensorFailureInstance => sensorFailureInstance.alerts);
    }
    
    const getSensorFailureInstancesWithDetails = (frameSensorReliabilitySummaries: FrameSensorReliabilitySummary[]) => {
        return frameSensorReliabilitySummaries.flatMap((frameSummary, frameIndex) =>
                [
                    ...frameSummary.throwSensorReliabilitySummaries.flatMap((throwSummary, throwIndex) =>
                        throwSummary.sensorFailureInstances.map((sensorFailureInstance, failureIndex) => ({
                            ...sensorFailureInstance,
                            frameSerialNumber: frameSummary.frameSerialNumber,
                            throwNumber: throwSummary.throwNumber,
                            category: SensorFailureCategory[sensorFailureInstance.category],
                            sensorType: SensorType[sensorFailureInstance.sensorType],
                            side: sensorFailureInstance.side !== undefined ? FlowDirection[sensorFailureInstance.side] : "",
                            timeFailed: sensorFailureInstance.timeFailed ? convertDateStringToLocalTime(sensorFailureInstance.timeFailed) : "",
                            estimatedHoursAtFailure: sensorFailureInstance.estimatedHoursAtFailure?.toFixed(2),
                            alerts: sensorFailureInstance.alerts.map((alert) => ({associatedAlertIndex: `${frameIndex}-${throwIndex}-${failureIndex}`, alertText: alert})),
                            associatedAlertIndex: `${frameIndex}-${throwIndex}-${failureIndex}`,
                        } as SensorFailureInstanceWithDetails))
                    ),
                    ...frameSummary.frameSensorFailureInstances.map((sensorFailureInstance, failureIndex) => ({
                        ...sensorFailureInstance,
                        frameSerialNumber: frameSummary.frameSerialNumber,
                        throwNumber: 0,
                        category: SensorFailureCategory[sensorFailureInstance.category],
                        sensorType: SensorType[sensorFailureInstance.sensorType],
                        side: sensorFailureInstance.side !== undefined ? FlowDirection[sensorFailureInstance.side] : "",
                        timeFailed: sensorFailureInstance.timeFailed ? convertDateStringToLocalTime(sensorFailureInstance.timeFailed) : "",
                        estimatedHoursAtFailure: sensorFailureInstance.estimatedHoursAtFailure?.toFixed(2),
                        alerts: sensorFailureInstance.alerts.map((alert) => ({associatedAlertIndex: `${frameIndex}-${failureIndex}`, alertText: alert})),
                        associatedAlertIndex: `${frameIndex}-${failureIndex}`,
                    } as SensorFailureInstanceWithDetails))
                ]
            )
    }
    
    const filterDataOnSegmentClick = (data: any) => {
        data.category.replace(/\s+/g, '') == categoryFilter?.filter ? setCategoryFilter(undefined) : setCategoryFilter({column: 'category', operator: 'equal', filter: data.category.replace(/\s+/g, '')});
    }
    
    return (
        <div className={'sensor-reliability-container'}>
            <div className={'report-filter'}>
                <div className="compressor-filter">
                    <Typography variant={'body1'}>Showing sensors for: </Typography>
                    <Autocomplete 
                        multiple
                        disableCloseOnSelect
                        disablePortal
                        options={compressorOptions}
                        onChange={(event, value: string[]) => setSelectedCompressors(value)}
                        sx={{backgroundColor: 'white', width: '70%', maxWidth: '100%'}}
                        renderInput={(params) => <TextField {...params} label="All Compressors" variant="outlined" />}
                    />
                </div>
                {isArielEmployee &&
                    <div style={{width: '30%'}}>
                        <OrganizationsAutoComplete 
                            updateSelectedOrg={(selectedOrg: Organization) => setSubOrg(selectedOrg)} 
                            parentOrganizations={parentOrganizations} selectedOrganization={subOrg} 
                        />
                    </div>
                }
            </div>
            {
                sensorFailureSummaries &&
                    <div className={'insight-card-container'}>
                        <DashboardCard label={'Total Sensors'} value={sensorFailureSummaries?.totalSensors ?? "null"} isLoading={sensorReliability.isLoading} icon={<SpeedIcon />} />
                        <DashboardCard label={'Failed Sensors'} value={sensorFailureSummaries?.failedSensors ?? "null"} isLoading={sensorReliability.isLoading} icon={<ErrorOutlineIcon />} />
                        <DashboardCard label={'New Failures'} value={sensorFailureSummaries?.newFailureCount ?? "null"} isLoading={sensorReliability.isLoading}
                           cardClassName={sensorFailureSummaries?.isNewFailuresGreaterThanZero ? 'new-failure-font-bad' : 'new-failure-font-good'}
                           icon={sensorFailureSummaries?.isNewFailuresGreaterThanZero? <TrendingUpIcon /> : <TrendingDownIcon />} />
                        <DashboardCard label={'Failure Rate'} value={sensorFailureSummaries?.failureRate ?? "null"} icon={<TimelineIcon />} />
                    </div>

            }
            <div>
                <Typography variant={'h6'} sx={{paddingBottom: '0.5em'}}>Failed Sensors by Type</Typography>
                <DonutChart data={failedSensorByTypeData} isLoading={sensorReliability.isLoading} showLegend={true} onSegmentClick={filterDataOnSegmentClick} />
            </div>
            <div>
                <Typography variant={'h6'} sx={{paddingBottom: '0.5em'}}>Failed Sensors</Typography>
                <div className={'e-card sensor-table'} >
                    <GenericReportGrid 
                        columns={sensorColumns} pageSize={100} data={sensorFailureDetails} isLoading={sensorReliability.isLoading} filter={categoryFilter}
                        dataExportFileName={"ASC_SensorReliability.xlsx"} childColumns={childColumns} childDataSource={associatedAlerts} queryString={'associatedAlertIndex'}
                        templateRows={templateRow}
                    />
                </div>
            </div>
        </div>
    )
}

export function formatNumberWithCommas(value: string | number): string {
    const numberValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numberValue)) {
        return value.toString();
    }
    return numberValue > 1000 ? numberValue.toLocaleString() : numberValue.toString();
}

export default CurrentSensorReliability;